import React, { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Close, ExpandMore, Search } from "@mui/icons-material";
import { Autocomplete, Button, CircularProgress, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { useDebouncedCallback } from "use-debounce";

import { selectAllFolders, selectCurrentAccount } from "fond/api";
import { SEARCH_KEY, SEARCH_SCOPE_FOLDER_ID_KEY, SEARCH_SCOPE_KEY } from "fond/constants";
import { useContents } from "fond/hooks/useContents";
import { useHotkeys } from "fond/hooks/useHotkeys";
import { useScopedSearchData } from "fond/hooks/useScopedSearchData";
import { ListSubheader, Paper, SeeAllResultsButton } from "fond/projects/GlobalSearch/Search.styles";
import SearchSuggestionsRow from "fond/projects/GlobalSearch/SearchSuggestionsRow";
import { MAX_SUGGESTIONS_PER_GROUP } from "fond/projects/GlobalSearch/shared";
import { updateFilter } from "fond/projects/redux";
import { Folder, ProjectPreview, Report } from "fond/types";
import { getPath } from "fond/utils/folder";
import { useAppDispatch } from "fond/utils/hooks";
import { searchCollectionByKey } from "fond/utils/search";

import { getScopeIcon, getScopeLabel } from "./searchScopeUtils";
import { SearchScope, useSearchScope } from "./useSearchScope";

import {
  Backdrop,
  CollapsedSearchTrigger,
  ExpandedAutocompleteWrapper,
  ExpandedSearchOverlay,
  ExpandedTextField,
  ScopeChip,
  SearchContainer,
} from "./GlobalSearch.styles";

interface SuggestionsList {
  projects: Array<ProjectPreview & { path: string[] }>;
  folders: Folder[];
  multiProjects: Folder[];
  reports: Array<Report & { path: string[] }>;
}

type SearchOption = SuggestionsList["projects"][number] | Folder | { EntityType: "all" } | SuggestionsList["reports"][number];

function getRealFolders(folders: Folder[]): Folder[] {
  return folders.filter((folder) => folder.EntityType === "folder" && !folder.MultiProject && !folder.MultiReport);
}

function getMultiProjectFolders(folders: Folder[]): Folder[] {
  return folders.filter((folder) => folder.EntityType === "folder" && folder.MultiProject);
}

const EMPTY_SUGGESTIONS: SuggestionsList = { projects: [], folders: [], multiProjects: [], reports: [] };
const EMPTY_COUNTS: Record<string, number> = { projects: 0, folders: 0, multiProjects: 0, reports: 0, all: 0 };

const GlobalSearch: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);

  const [isExpanded, setIsExpanded] = useState(false);
  const [value, setValue] = useState("");
  const [suggestionsList, setSuggestionsList] = useState<SuggestionsList>(EMPTY_SUGGESTIONS);
  const [resultCount, setResultCount] = useState<Record<string, number>>(EMPTY_COUNTS);
  const [open, setOpen] = useState(false);
  const [scopeMenuAnchorEl, setScopeMenuAnchorEl] = useState<HTMLElement | null>(null);
  const { isLoading } = useContents();
  const { availableScopes, activeScope, setActiveScope } = useSearchScope();
  const hasMultipleScopes = availableScopes.length > 1;
  const workspaceScope: SearchScope = useMemo(() => ({ type: "workspace", folderId: null, folderName: null }), []);

  const currentAccount = useSelector(selectCurrentAccount);

  // Scoped data from shared hook
  const allFolders = useSelector(selectAllFolders);
  const { projects: scopedProjects, folders: scopedSubfolders, reports: scopedReports } = useScopedSearchData(activeScope);

  const realFolders = useMemo(() => getRealFolders(allFolders), [allFolders]);
  const scopedRealFolders = useMemo(() => getRealFolders(scopedSubfolders), [scopedSubfolders]);
  const scopedMultiProjects = useMemo(() => getMultiProjectFolders(scopedSubfolders), [scopedSubfolders]);

  const closeOptions = () => setOpen(false);

  const openOverlay = useCallback(() => {
    setIsExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  // "/" hotkey to open the search
  useHotkeys({
    action: "SEARCH",
    callback: (event) => {
      event.preventDefault();
      openOverlay();
    },
    enabled: !isExpanded && !isLoading,
  });

  const collapse = useCallback(() => {
    setIsExpanded(false);
    setValue("");
    setSuggestionsList(EMPTY_SUGGESTIONS);
    setResultCount(EMPTY_COUNTS);
    setOpen(false);
  }, []);

  const expand = useCallback(() => {
    if (isLoading) return;
    openOverlay();
  }, [isLoading, openOverlay]);

  const handleSearchTrigger = useCallback(() => {
    if (value) {
      collapse();
      const params = new URLSearchParams();
      params.set(SEARCH_KEY, value);
      params.set(SEARCH_SCOPE_KEY, activeScope.type);
      if (activeScope.folderId) {
        params.set(SEARCH_SCOPE_FOLDER_ID_KEY, activeScope.folderId);
      }
      navigate(`/search?${params.toString()}`);
    }
  }, [value, collapse, navigate, activeScope]);

  const handleOnChange = useCallback(
    (_: React.SyntheticEvent<Element, Event>, selected: SearchOption) => {
      if (selected?.EntityType === "all") {
        return;
      }

      const resourceId = selected?.ID;
      if (selected?.EntityType === "folder") {
        const selectedInCurrentAccount = selected.Account.ID === currentAccount?.ID;
        dispatch(updateFilter(selectedInCurrentAccount ? "workspace" : "shared"));
        collapse();
        navigate(`/folders/${resourceId}`);
        return;
      }
      if (selected?.EntityType === "report") {
        collapse();
        navigate(`/reports/${resourceId}`);
        return;
      }
      collapse();
      navigate(`/project/${resourceId}`);
    },
    [currentAccount, dispatch, collapse, navigate]
  );

  const setSuggestionsListDebounced = useDebouncedCallback((searchValue: string) => {
    const suggestedProjects = searchCollectionByKey(scopedProjects, searchValue, "ProjectName");
    const suggestedFolders = searchCollectionByKey(scopedRealFolders, searchValue, "Name");
    const suggestedMultiProjects = searchCollectionByKey(scopedMultiProjects, searchValue, "Name");
    const suggestedReports = searchCollectionByKey(scopedReports, searchValue, "Name");

    const allResultsCount = suggestedProjects.length + suggestedFolders.length + suggestedMultiProjects.length + suggestedReports.length;
    setResultCount({
      projects: suggestedProjects.length,
      folders: suggestedFolders.length,
      multiProjects: suggestedMultiProjects.length,
      reports: suggestedReports.length,
      all: allResultsCount,
    });

    setSuggestionsList({
      projects: suggestedProjects
        .slice(0, MAX_SUGGESTIONS_PER_GROUP)
        .map((project) => ({ ...project, path: getPath(realFolders, project.FolderID) })),
      folders: suggestedFolders.slice(0, MAX_SUGGESTIONS_PER_GROUP),
      multiProjects: suggestedMultiProjects.slice(0, MAX_SUGGESTIONS_PER_GROUP),
      reports: suggestedReports.slice(0, MAX_SUGGESTIONS_PER_GROUP).map((report) => ({ ...report, path: getPath(realFolders, report.Folder?.ID) })),
    });
  }, 500);

  const handleInputChange = useCallback(
    async (searchValue: string) => {
      setValue(searchValue);
      if (searchValue) {
        setOpen(true);
      } else {
        setOpen(false);
      }
      setSuggestionsListDebounced(searchValue);
    },
    [setSuggestionsListDebounced]
  );

  const handleScopeMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setScopeMenuAnchorEl(event.currentTarget);
  }, []);

  const handleScopeMenuClose = useCallback(() => {
    setScopeMenuAnchorEl(null);
  }, []);

  const handleScopeSelect = useCallback(
    (scope: SearchScope) => {
      setActiveScope(scope);
      setScopeMenuAnchorEl(null);
      if (value) {
        setSuggestionsListDebounced(value);
      }
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [setActiveScope, value, setSuggestionsListDebounced]
  );

  const getOptionLabel = (option: SearchOption) => {
    switch (option.EntityType) {
      case "folder":
        return option.Name;
      case "project":
        return option.ProjectName;
      case "report":
        return option.Name;
      default:
        return value;
    }
  };

  const hasTruncatedGroup = useMemo(() => {
    return (
      suggestionsList.projects.length === MAX_SUGGESTIONS_PER_GROUP ||
      suggestionsList.multiProjects.length === MAX_SUGGESTIONS_PER_GROUP ||
      suggestionsList.folders.length === MAX_SUGGESTIONS_PER_GROUP ||
      suggestionsList.reports.length === MAX_SUGGESTIONS_PER_GROUP
    );
  }, [suggestionsList]);

  const options: SearchOption[] = [
    ...suggestionsList.projects,
    ...suggestionsList.multiProjects,
    ...suggestionsList.folders,
    ...suggestionsList.reports,
    ...(hasTruncatedGroup ? [{ EntityType: "all" as const }] : []),
  ];

  const getGroupCountForOption = (option: SearchOption): number => {
    if (option.EntityType === "folder") {
      if (option.MultiProject !== null) {
        return resultCount.multiProjects;
      }
      return resultCount.folders;
    }
    if (option.EntityType === "project") return resultCount.projects;
    if (option.EntityType === "report") return resultCount.reports;
    if (option.EntityType === "all") return resultCount.all;
    return 0;
  };

  const getGroupBy = (option: SearchOption): string => {
    const count = getGroupCountForOption(option);
    if (option.EntityType === "folder" && option.MultiProject !== null) {
      return `MARKET LENS (${count})`;
    }
    return `${option.EntityType.toLocaleUpperCase()} (${count})`;
  };

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        collapse();
      } else if (event.key === "Enter") {
        handleSearchTrigger();
      } else if ((event.key === "ArrowUp" || event.key === "ArrowDown") && !value && hasMultipleScopes) {
        event.preventDefault();
        const currentIndex = availableScopes.findIndex((s) => s.type === activeScope.type && s.folderId === activeScope.folderId);
        const direction = event.key === "ArrowDown" ? 1 : -1;
        const nextIndex = (currentIndex + direction + availableScopes.length) % availableScopes.length;
        setActiveScope(availableScopes[nextIndex]);
      }
    },
    [collapse, handleSearchTrigger, value, hasMultipleScopes, availableScopes, activeScope, setActiveScope]
  );

  const scopeStartAdornment = (
    <>
      <Search sx={{ color: "action.active", mr: 0.5 }} />
      {hasMultipleScopes && (
        <>
          <ScopeChip
            data-testid="topbar-search-scope"
            icon={getScopeIcon(activeScope)}
            label={getScopeLabel(activeScope)}
            size="small"
            onClick={handleScopeMenuOpen}
            deleteIcon={<ExpandMore sx={{ fontSize: 16 }} />}
            onDelete={handleScopeMenuOpen}
          />
          <Menu
            anchorEl={scopeMenuAnchorEl}
            open={Boolean(scopeMenuAnchorEl)}
            onClose={handleScopeMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            disableScrollLock
          >
            {availableScopes.map((scope) => (
              <MenuItem
                key={`${scope.type}-${scope.folderId}`}
                selected={scope.type === activeScope.type && scope.folderId === activeScope.folderId}
                onClick={() => handleScopeSelect(scope)}
              >
                <ListItemIcon>{getScopeIcon(scope)}</ListItemIcon>
                <ListItemText>{getScopeLabel(scope)}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </>
  );

  if (!isExpanded) {
    return (
      <SearchContainer>
        <CollapsedSearchTrigger data-testid="topbar-search-collapsed" onClick={expand}>
          {isLoading ? <CircularProgress size={20} color="inherit" /> : <Search sx={{ fontSize: 18 }} />}
          {isLoading ? "Loading..." : "Type / to search"}
        </CollapsedSearchTrigger>
      </SearchContainer>
    );
  }

  return (
    <>
      <Backdrop data-testid="topbar-search-backdrop" onClick={collapse} />
      <ExpandedSearchOverlay data-testid="topbar-search-expanded">
        <IconButton onClick={collapse} sx={{ color: "white", mr: 1 }} data-testid="topbar-search-close">
          <Close />
        </IconButton>

        <ExpandedAutocompleteWrapper>
          <Autocomplete
            data-testid="topbar-search"
            id="topbar-search"
            disableClearable
            open={open}
            noOptionsText={
              activeScope.type !== "workspace" ? (
                <>
                  No results found. Try searching the{" "}
                  <Button
                    variant="text"
                    size="small"
                    sx={{ textTransform: "none", p: 0, minWidth: "auto", verticalAlign: "baseline" }}
                    onClick={() => handleScopeSelect(workspaceScope)}
                  >
                    Workspace
                  </Button>
                </>
              ) : (
                "No results found"
              )
            }
            inputValue={value}
            onClose={(_, reason) => {
              if (reason !== "blur") {
                closeOptions();
              }
            }}
            onChange={handleOnChange}
            onKeyDown={handleKeyDown}
            onInputChange={(_, newValue, reason) => {
              if (reason === "input") {
                handleInputChange(newValue);
              }
            }}
            options={options}
            filterOptions={(filteredOptions) => filteredOptions}
            groupBy={getGroupBy}
            getOptionLabel={getOptionLabel}
            PaperComponent={(props) => <Paper {...props} />}
            renderInput={(params) => (
              <ExpandedTextField
                {...params}
                inputRef={inputRef}
                autoFocus
                variant="outlined"
                placeholder="Search for anything you have access to..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: scopeStartAdornment,
                  endAdornment: null,
                }}
              />
            )}
            renderOption={(props, option) =>
              option.EntityType === "all" ? (
                <SeeAllResultsButton {...props} data-testid="see-all-results" key="ALL" onClick={handleSearchTrigger}>
                  see all results
                </SeeAllResultsButton>
              ) : (
                <SearchSuggestionsRow {...props} option={option} onClick={collapse} key={option.ID} />
              )
            }
            renderGroup={(params) => (
              <React.Fragment key={params.group}>
                {params.group !== "ALL" && <ListSubheader component="div">{params.group}</ListSubheader>}
                {params.children}
              </React.Fragment>
            )}
          />
        </ExpandedAutocompleteWrapper>
      </ExpandedSearchOverlay>
    </>
  );
};

export default GlobalSearch;
